import dayjs, { type Dayjs } from 'dayjs';

/* ============================================================
 * Scope window math. Every preset resolves to an explicit
 * [from, to] day pair (inclusive), and every window has a
 * "previous equivalent" of the same length ending the day
 * before it starts — the denominator of every delta.
 * ============================================================ */

export type RangePreset = 'week' | 'lastweek' | 'month' | '30d' | 'custom';

export const RANGE_PRESETS: Array<{ key: RangePreset; label: string }> = [
  { key: 'week', label: 'This week' },
  { key: 'lastweek', label: 'Last week' },
  { key: 'month', label: 'This month' },
  { key: '30d', label: 'Last 30 days' },
  { key: 'custom', label: 'Custom' },
];

export interface ScopeWindow {
  /** YYYY-MM-DD, inclusive. */
  from: string;
  /** YYYY-MM-DD, inclusive. */
  to: string;
}

const DAY = 'YYYY-MM-DD';

/** Monday of the week containing `d` (business weeks run Mon–Sun). */
function mondayOf(d: Dayjs): Dayjs {
  return d.subtract((d.day() + 6) % 7, 'day');
}

export function resolveWindow(
  preset: RangePreset,
  custom?: Partial<ScopeWindow>,
  today: Dayjs = dayjs()
): ScopeWindow {
  const t = today.startOf('day');
  switch (preset) {
    case 'week':
      return { from: mondayOf(t).format(DAY), to: t.format(DAY) };
    case 'lastweek': {
      const monday = mondayOf(t).subtract(7, 'day');
      return { from: monday.format(DAY), to: monday.add(6, 'day').format(DAY) };
    }
    case 'month':
      return { from: t.startOf('month').format(DAY), to: t.format(DAY) };
    case '30d':
      return { from: t.subtract(29, 'day').format(DAY), to: t.format(DAY) };
    case 'custom': {
      const to = custom?.to ?? t.format(DAY);
      const from = custom?.from ?? dayjs(to).subtract(6, 'day').format(DAY);
      return { from, to };
    }
  }
}

/** Same-length window immediately preceding `window` (for deltas). */
export function previousWindow(window: ScopeWindow): ScopeWindow {
  const from = dayjs(window.from);
  const days = dayjs(window.to).diff(from, 'day') + 1;
  const prevTo = from.subtract(1, 'day');
  return { from: prevTo.subtract(days - 1, 'day').format(DAY), to: prevTo.format(DAY) };
}

/** Human label, e.g. "1–30 Jun 2026" or "29 Jun – 5 Jul 2026". */
export function windowLabel(window: ScopeWindow): string {
  const from = dayjs(window.from);
  const to = dayjs(window.to);
  if (from.isSame(to, 'day')) return from.format('D MMM YYYY');
  if (from.isSame(to, 'month')) return `${from.format('D')}–${to.format('D MMM YYYY')}`;
  if (from.isSame(to, 'year')) return `${from.format('D MMM')} – ${to.format('D MMM YYYY')}`;
  return `${from.format('D MMM YYYY')} – ${to.format('D MMM YYYY')}`;
}

/** Comparison phrasing for delta chips, e.g. "vs last week". */
export function deltaPhrase(preset: RangePreset): string {
  switch (preset) {
    case 'week':
      return 'vs last week';
    case 'lastweek':
      return 'vs week before';
    case 'month':
      return 'vs last month';
    case '30d':
      return 'vs prior 30 days';
    case 'custom':
      return 'vs prior period';
  }
}
